"use client";

import { Cross, PillSelector } from "@monsterindia/global-ui-components";
import { memo, useEffect, useMemo, useRef } from "react";

import { NotificationCategory } from "../../types";
import "./index.css";

type CategoryFiltersProps = {
  categories: Record<
    NotificationCategory,
    { count: number; notificationTypes?: string[] } | undefined
  >;
  selectedCategory: NotificationCategory;
  onCategoryChange: (category: NotificationCategory) => void;
};

type PillOption = {
  id?: string;
  name?: string;
  text?: string;
  count?: number;
  selected?: boolean;
};

const categoryLabels: Record<string, string> = {
  ALL: "All",
  PROFILE_VIEWED: "Profile views",
  PROFILE_SAVED: "Profile saved",
  DOWNLOADED_CV: "Resume downloaded",
  PROFILE_CONTACT_VIEWED: "Contacted viewed",
  PROFILE_CONTACTED: "Contacted",
};

const CategoryFilters: React.FC<CategoryFiltersProps> = ({
  categories,
  selectedCategory,
  onCategoryChange,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const options: PillOption[] = useMemo(() => {
    return Object.entries(categories)
      .filter(
        ([, value]) =>
          value !== undefined &&
          value !== null &&
          typeof value === "object" &&
          "count" in value &&
          value.count > 0,
      )
      .map(([key, value]) => ({
        id: key,
        text: `${categoryLabels[key] || key}`,
        count: value?.count || 0,
        selected: selectedCategory === key,
      }));
  }, [categories, selectedCategory]);

  // Scroll selected filter into view on mount
  useEffect(() => {
    if (!containerRef.current) return;

    const timeoutId = setTimeout(() => {
      if (!containerRef.current) return;

      const selectedIndex = options.findIndex((opt) => opt.selected);
      if (selectedIndex <= 0) return;

      const pillButtons = containerRef.current.querySelectorAll("button");
      const targetPill = pillButtons[selectedIndex];

      if (targetPill) {
        targetPill.scrollIntoView({
          behavior: "instant",
          block: "nearest",
          inline: "center",
        });
      }
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [selectedCategory, options]);

  const handleSelect = (selectedOption?: PillOption[]) => {
    const nextCategory =
      selectedOption && selectedOption[0]?.id
        ? (selectedOption[0].id as NotificationCategory)
        : "ALL";
    onCategoryChange(nextCategory);
  };

  if (!options.length) {
    return null;
  }

  return (
    <div className="pill-container" ref={containerRef}>
      <PillSelector
        getValue={(selectedOption: PillOption[]) =>
          handleSelect(selectedOption)
        }
        options={options}
        rightIcon={
          <Cross
            className="cursor-pointer"
            onClick={() => handleSelect(undefined)}
          />
        }
      />
    </div>
  );
};

export default memo(CategoryFilters);
